import { ChangeEvent } from "react"
import { useRecoilState, useRecoilValue } from "recoil"
import { FaMagnifyingGlass } from "react-icons/fa6"
import {
  Box,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Heading
} from "@chakra-ui/react"
import HamburgerNavMenu from "src/components/common/HamburgerNavMenu"
import { gridHeaderSearchTextAtom } from "src/recoil/atoms/avatars"
import HomeBackIcon from "../../home/HomeBackIcon"
import useGridPageStyling from "../hooks/useGridPageStyling"
import usePageStyling from "src/hooks/usePageStyling"
import { useIsMobileDevice } from "src/util/browser"
import { useIsMobileApp } from "src/util/deviceType"
import { nativeMobileSafeAreaAtom } from "src/recoil/atoms/nativeMobileSafeArea"
import { ConditionalAppLogo } from "src/conditionals/Authentication/components/ConditionalAppLogo"

const SearchBar = () => {
  const [gridHeaderSearchText, setGridHeaderSearchTextAtom] = useRecoilState(
    gridHeaderSearchTextAtom
  )

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setGridHeaderSearchTextAtom(value)
  }

  return (
    <Box w={"full"} maxW="746px" px={"12px"} color="white">
      <InputGroup>
        <InputLeftElement pointerEvents="none" color="#888888">
          <FaMagnifyingGlass />
        </InputLeftElement>
        <Input
          value={gridHeaderSearchText}
          onChange={handleOnChange}
          variant="outline"
          width={"100%"}
          height="38px"
          placeholder="Search"
          borderColor="#888888"
          _focus={{
            outline: "none"
          }}
          userSelect="all"
        />
      </InputGroup>
    </Box>
  )
}

type UIGridHeaderProps = {
  navigatedFromJourney?: boolean
  showSearchBox?: boolean
  headerTitle?: string
  showBackButton?: boolean
  customBackAction?: () => void
}

const UIGridHeader = ({
  navigatedFromJourney = false,
  showSearchBox = false,
  headerTitle,
  showBackButton = false,
  customBackAction
}: UIGridHeaderProps) => {
  const isMobile = useIsMobileDevice()
  const isMobileApp = useIsMobileApp()
  const style = useGridPageStyling()
  const branding = usePageStyling("authPageStyling")

  const renderLogoOrBackIcon = () => {
    if (showBackButton || navigatedFromJourney) {
      return (
        <HomeBackIcon
          JoinPageButtonMouseoverTextColor={
            branding?.authPageButtonMouseoverTextColor
          }
          onClick={customBackAction ?? undefined}
          JoinPageButtonTextColor={branding?.authPageButtonTextColor}
          joinButtonBackgroundColor={branding?.authPageButtonBackgroundColor}
          joinButtonBorderColor={branding?.authPageButtonBorderColor}
          joinButtonMouseoverBackgroundColor={
            branding?.authPageButtonMouseoverBackgroundColor
          }
          navigatedFromiOS={isMobileApp && !navigatedFromJourney}
          position="unset"
          // top={null}
          left={"8px"}
        />
      )
    }

    return <ConditionalAppLogo showLogo={isMobile} />
  }

  const safeArea = useRecoilValue(nativeMobileSafeAreaAtom)

  return (
    <>
      {/* A black bg that stretches to the top to fill in the gap caused by safearea */}
      <Box
        pos="absolute"
        bg="black"
        w="100%"
        h={`${safeArea.top}px`}
        top={0}
      ></Box>
      <Flex
        pos="sticky"
        w="100%"
        h="auto"
        top={0}
        zIndex={1}
        bg="black"
        px={["8px", "12px", "12px"]}
        py={["9px", null, "15px"]}
        minHeight={["58px", "unset", "unset"]}
        justifyContent="space-between"
        align="center"
      >
        {renderLogoOrBackIcon()}

        {showSearchBox && <SearchBar />}
        {headerTitle && (
          <Heading
            color="white"
            fontWeight={"semibold"}
            fontSize={{ base: "22px", md: "35px" }}
            pos="absolute"
            left="50%"
            transform="translateX(-50%)"
          >
            {headerTitle}
          </Heading>
        )}
        <HamburgerNavMenu
          items={["account", "plans", "logout"]}
          color={style.panelTextColor ?? "#1AC541"}
          linkColor={branding?.authPagePanelTextColor ?? "#1AC541"}
          bgColor="#000000"
          top={{}}
          right={{ base: "8px", md: "24px" }}
          position="unset"
          popoverBg={style.panelBackgroundColor}
        />
      </Flex>
    </>
  )
}

export default UIGridHeader
